import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import "../styles/AssessmentForm.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SUBMODALITIES_MAP = {
     USG: ["Sub1", "Sub2"],
     Mammography: ["Sub1", "Sub2"],
     BMD: ["Sub1", "Sub2"],
     CT: ["Sub1", "Sub2"],
     MRI: ["Sub1", "Sub2"],
};

export default function AssessmentEditForm({ onCancel, onSave }) {
     const { candidateId } = useParams();
     const submittingRef = useRef(false);
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const navigate = useNavigate();
     const [isSubmitting, setIsSubmitting] = useState(false);
     const [isLoading, setIsLoading] = useState(true);
     const [modalities, setModalities] = useState([]);

     const [selectedModality, setSelectedModality] = useState("");
     const [status, setStatus] = useState('');
     const [currentSelections, setCurrentSelections] = useState({});
     const [selectedRegions, setSelectedRegions] = useState({
          regionsData: {}, // Stores the branch selections
          regionsIds: {}   // Stores the IDs for each region-branch combination
     });

     const [personal, setPersonal] = useState({
          positionApplied: "",
          candidateName: "",
          mobile: "",
          email: "",
          qualification: "",
          registration: "",
          paediatric: "",
          totalExperience: "",
          department: "",
          vdcLocation: "",
          association_type: "",
          timing_from: "",
          timing_to: "",
          hr_status: "",
     });

     const [technical, setTechnical] = useState({
          CT: "",
          MRI: "",
          RadiographyReporting: "",
          USG: "",
          assessmentCT: "",
          assessmentMRI: "",
          assessmentRadiography: "",
          assessmentUSG: "",
          idCT: null,
          idMRI: null,
          idRadiography: null,
          idUSG: null
     });

     const [hiring, setHiring] = useState({
          decision: "",
     });

     const [notes, setNotes] = useState({
          overallNotes: "",
     });

     const [personality, setPersonality] = useState({
          attitude: "",
          jobKnowledge: "",
          communication: "",
          confidence: "",
          personality: "",
          flexibility: "",
          idAttitude: null,
          idJobKnowledge: null,
          idCommunication: null,
          idConfidenc: null,
          idPersonlity: null,
          idPersonlity: null,
     });

     const [errors, setErrors] = useState({});

     // Fetch candidate data
     useEffect(() => {
          const fetchCandidateData = async () => {
               try {
                    setIsLoading(true);
                    const response = await fetch(`${baseUrl}/getCandidateFullDetails/${candidateId}`, {
                         headers: {
                              "Authorization": `Bearer ${sessionStorage.getItem("token")}`,
                         },
                    });

                    if (!response.ok) {
                         throw new Error('Failed to fetch candidate data');
                    }

                    const result = await response.json();
                    console.log("API Response:", result); // Debug log

                    if (result.candidate) {
                         populateFormData(result);
                    }
               } catch (error) {
                    console.error("Error fetching candidate data:", error);
                    toast.error("Failed to load candidate data");
               } finally {
                    setIsLoading(false);
               }
          };

          if (candidateId) {
               fetchCandidateData();
          }
     }, [candidateId, baseUrl]);

     const populateFormData = (data) => {
          const candidate = data.candidate;

          // Personal details - mapping API fields to form fields
          setPersonal({
               positionApplied: candidate.position_applied || "",
               candidateName: candidate.candidate_name || "",
               mobile: candidate.mobile || "",
               email: candidate.email || "",
               qualification: candidate.education_qualification || "",
               registration: candidate.medical_registration === "Y" ? "Yes" : "No",
               paediatric: candidate.Paediatric === "Y" ? "Yes" : "No",
               totalExperience: candidate.total_experience || "",
               department: candidate.department || "",
               vdcLocation: candidate.vdc_location || "",
               association_type: candidate.association_type || "",
               timing_from: candidate.timing_from ? candidate.timing_from.substring(0, 5) : "09:00",
               timing_to: candidate.timing_to ? candidate.timing_to.substring(0, 5) : "17:00",
               hr_status: candidate.hr_status || "",
          });

          // Notes
          setNotes({
               overallNotes: candidate.overallNotes || "",
          });

          // Hiring decision
          setHiring({
               decision: candidate.hr_status || candidate.rpt_mgr_status || "Onhold",
          });

          // Modalities - transform API data to match form structure
          if (data.modalities && Array.isArray(data.modalities)) {
               const modalitiesGrouped = data.modalities.reduce((acc, item) => {
                    const modality = item.modality;
                    if (!acc[modality]) {
                         acc[modality] = {
                              modality: modality,
                              selections: {}
                         };
                    }
                    acc[modality].selections[item.selection_area] = {
                         applicable: item.applicable,
                         level: item.level,
                         id: item.id || null
                    };
                    return acc;
               }, {});

               setModalities(Object.values(modalitiesGrouped));
          }

          // Technical evaluations
          if (data.technical_evaluations && Array.isArray(data.technical_evaluations)) {
               const techData = data.technical_evaluations.reduce((acc, item) => {
                    switch (item.particular) {
                         case "Radiography Reporting":
                              acc.assessmentRadiography = item.assessment || "";
                              acc.RadiographyReporting = item.notes || "";
                              acc.idRadiography = item.id || null;
                              break;
                         case "CT Scans Reporting":
                              acc.assessmentCT = item.assessment || "";
                              acc.CT = item.notes || "";
                              acc.idCT = item.id || null;
                              break;
                         case "MRI Scans Reporting":
                              acc.assessmentMRI = item.assessment || "";
                              acc.MRI = item.notes || "";
                              acc.idMRI = item.id || null;
                              break;
                         case "USG Scanning":
                              acc.assessmentUSG = item.assessment || "";
                              acc.USG = item.notes || "";
                              acc.idUSG = item.id || null;
                              break;
                    }
                    return acc;
               }, {});
               setTechnical(prev => ({ ...prev, ...techData }));
          }

          // Personality assessments
          if (data.personality_assessments && Array.isArray(data.personality_assessments)) {
               const personalityData = data.personality_assessments.reduce((acc, item) => {
                    switch (item.criteria) {
                         case "Attitude":
                              acc.attitude = item.remarks || "";
                              acc.idAttitude = item.id || null;
                              break;
                         case "Job Knowledge":
                              acc.jobKnowledge = item.remarks || "";
                              acc.idJobKnowledge = item.id || null;
                              break;
                         case "Communication Skills":
                              acc.communication = item.remarks || "";
                              acc.idCommunication = item.id || null;
                              break;
                         case "Confidence":
                              acc.confidence = item.remarks || "";
                              acc.idConfidenc = item.id || null;
                              break;
                         case "Personality":
                              acc.personality = item.remarks || "";
                              acc.idPersonlity = item.id || null;
                              break;
                         case "Flexibility":
                              acc.flexibility = item.remarks || "";
                              acc.idFlexibility = item.id || null;
                              break;
                    }
                    return acc;
               }, {});
               setPersonality(prev => ({ ...prev, ...personalityData }));
          }

          // Regions - transform API data to match form structure
          // Regions - transform API data to match form structure
          if (data.regions && Array.isArray(data.regions)) {
               const regionsData = data.regions.reduce((acc, item) => {
                    const region = item.region_name;
                    if (!acc[region]) {
                         acc[region] = [];
                    }
                    acc[region].push(item.branch_name);
                    return acc;
               }, {});

               // Store IDs for each region-branch combination
               const regionsIds = data.regions.reduce((acc, item) => {
                    const key = `${item.region_name}-${item.branch_name}`;
                    acc[key] = item.id || null;
                    return acc;
               }, {});

               setSelectedRegions({
                    regionsData: regionsData,
                    regionsIds: regionsIds
               });
          }
     };



     function handlePersonalChange(e) {
          const { name, value } = e.target;
          setPersonal((p) => ({ ...p, [name]: value }));
     }





     async function handleSubmit(e) {
          e.preventDefault();
          if (isSubmitting) return;

          submittingRef.current = true;
          setIsSubmitting(true);

          // Transform data back to API format
          const payload = {
               personal: {
                    position_applied: personal.positionApplied,
                    candidate_name: personal.candidateName,
                    mobile: personal.mobile,
                    email: personal.email,
                    education_qualification: personal.qualification,
                    medical_registration: personal.registration === "Yes" ? "Y" : "N",
                    Paediatric: personal.paediatric === "Yes" ? "Y" : "N",
                    total_experience: personal.totalExperience,
                    department: personal.department,
                    vdc_location: personal.vdcLocation,
                    association_type: personal.association_type,
                    timing_from: personal.timing_from + ":00",
                    timing_to: personal.timing_to + ":00",
                    hr_status: personal.hr_status,

               },
               modalities: modalities.flatMap(entry =>
                    Object.entries(entry.selections).map(([selection_area, data]) => ({
                         id: data?.id || entry?.id,
                         modality: entry.modality,
                         selection_area,
                         applicable: data.applicable,
                         level: data.level
                    }))
               ),
               technical_evaluations: [
                    {
                         id: technical.idRadiography || null, // ✅ Include ID
                         particular: "Radiography Reporting",
                         assessment: technical.assessmentRadiography,
                         notes: technical.RadiographyReporting
                    },
                    {
                         id: technical.idCT || null, // ✅ Include ID
                         particular: "CT Scans Reporting",
                         assessment: technical.assessmentCT,
                         notes: technical.CT
                    },
                    {
                         id: technical.idMRI || null, // ✅ Include ID
                         particular: "MRI Scans Reporting",
                         assessment: technical.assessmentMRI,
                         notes: technical.MRI
                    },
                    {
                         id: technical.idUSG || null, // ✅ Include ID
                         particular: "USG Scanning",
                         assessment: technical.assessmentUSG,
                         notes: technical.USG
                    }
               ],
               personality_assessments: [
                    { id: personality.idAttitude || null, criteria: "Attitude", remarks: personality.attitude },
                    { id: personality.idJobKnowledge || null, criteria: "Job Knowledge", remarks: personality.jobKnowledge },
                    { id: personality.idCommunication || null, criteria: "Communication Skills", remarks: personality.communication },
                    { id: personality.idConfidenc || null, criteria: "Confidence", remarks: personality.confidence },
                    { id: personality.idPersonlity || null, criteria: "Personality", remarks: personality.personality },
                    { id: personality.idFlexibility || null, criteria: "Flexibility", remarks: personality.flexibility }
               ],
               regions: Object.entries(selectedRegions.regionsData || {}).flatMap(([region_name, branches]) =>
                    branches.map(branch_name => ({
                         region_name,
                         branch_name,
                         id: selectedRegions.regionsIds[`${region_name}-${branch_name}`] || ''
                    }))
               )
          };

          try {
               const response = await fetch(`${baseUrl}/updateCandidateDetailsId/${candidateId}`, {
                    method: "POST",
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${sessionStorage.getItem("token")}`,
                    },
                    body: JSON.stringify(payload),
               });

               if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || "Update failed");
               }

               const result = await response.json();
               toast.success("Assessment updated successfully!");

               setTimeout(() => {
                    navigate("/hr-radiology-assessment", {
                         state: { successMessage: "Assessment successfully updated!" },
                    });
               }, 1000);

          } catch (err) {
               console.error("Update failed:", err);
               toast.error(err.message || "Failed to update assessment");
          } finally {
               submittingRef.current = false;
               setIsSubmitting(false);
          }
     }

     function handleCancel() {
          navigate('/hr-radiology-assessment');
     }

     // Your existing constants and styles...
     const personalityOptions = [
          "Outstanding",
          "Exceeds Expectation",
          "Meets Expectation",
          "Does not meet expectation",
     ];

     const cardStyle = {
          background: "#fff",
          padding: "5px",
          borderRadius: "8px",
          boxShadow: "0 2px 6px rgb(0 0 0 / 0.1)",
          marginBottom: "5px",
     };

     const grid4Columns = {
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "5px",
          alignItems: "start",
     };

     const grid6Columns = {
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: "16px",
          alignItems: "start",
     };


     if (isLoading) {
          return (
               <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh',
                    fontSize: '18px',
                    color: '#666'
               }}>
                    Loading candidate data...
               </div>
          );
     }

     const MODALITY_MAP = {
          XRAY: [
               "Plain Radiography - Basic",
               "Plain Radiography - Advanced",
               "Lower limb scanogram",
               "Whole spine X-ray",
               "Boneage"
          ],
          XRAYProcedure: [
               "Barium Study",
               "Distal Loopogram/ Fistulogram",
               "HSG",
               "IVP",
               "MCUG",
               "RGU"
          ],
          Mammography: [
               "Mammography",
               "Mammography with out screening"
          ],
          BMD: [
               "Mammography"
          ],
          USG: [
               "Basic",
               "Doppler Studies",
               "Thyroid",
               "Scrotum USG",
               "Breast USG",
               "TIFFA",
               "Foetal Echo",
               "MSK USG",
               "Penile Doppler",
               "TRUS",
               "USG Guided FNAC",
               "USG Guided Trucut biopsy",
               "USG Guided aspiration",
               "Breast Biopsy",
               "TRUS guided Biopsy"
          ],
          CT: [
               "Brain",
               "PNS",
               "Orbits",
               "Temporal Bone",
               "Neck",
               "KUB",
               "Spine",
               "Chest",
               "Abdomen",
               "DECT",
               "3D CT",
               "CT Angiography",
               "Coronary CT",
               "Virtual Bronchoscopy",
               "CT Perfusion",
               "CT Screening",
               "Bronchoscopy / Colonoscopy",
               "CT Guided Biopsy",
               "Virtual Bronchoscopy / Colonoscopy"
          ],
          MRI: [
               "Brain",
               "Spine",
               "Ankle",
               "Elbow",
               "Hip",
               "Knee",
               "Wrist",
               "Foot",
               "Shoulder",
               "SI Joint",
               "MSK -Small Joints",
               "MRI Extremities",
               // "Boss Protocol - PNS",
               "DBS Protocol",
               "Dernentia Protocol",
               "Epdepsy Protocol",
               "Headache Protocol",
               "BIH/lIH Protocol",
               "MRI Cochlear Implant Protocol",
               "Stroke Protocol",
               "Vertigo Protocol",
               "Visual Pathway Protocol",
               "Abdomen",
               "Brain with CSF",
               "Cardiac MRI",
               "Chest",
               "Dynamic Sleep Study",
               "Fibroid Mapping",
               "FMR + DTI + MRS",
               "FMRI",
               "Foetal MRI",
               "Lymphangiography",
               "Movement Disorder",
               "MRCP",
               "MRI Angiographyhain",
               "MRI Arhrogram",
               "MRI Breast",
               "MRI Cartigram",
               "MRI Cistenography",
               "MRI Defaccography",
               "MRI Enterography / MRI Enteroclysis",
               "MRI Face",
               "MRI Fistulogram",
               "MRI Liver",
               "MRI Neurography",
               "MRI Perfusion",
               "MRI Peripheral Angiography",
               "MRI Screcning",
               "MRI TM Joints",
               "MRI Tractography",
               "MRI Urography",
               // "MSK",
               "Neck",
               "Pelvis",
               "PNS",
               "Prostate",
               "Sella/Pituitary",
               "Soft Tissue MRI",
               "Spectroscopy",
               "Temporal Bone",
               "Vessel Wall",
               "OverallEvaluation Notes",
               "Whole Body"
          ],
          "Special Procedures": [
               "RGU",
               "MCUG",
               "Barium Swallow",
               "Barium Enema",
               "HSG",
          ],
     };


     return (
          <form onSubmit={handleSubmit}>
               <ToastContainer />
               <div style={{
                    minHeight: "40vh",
                    padding: 5,
                    overflowY: "auto",
                    backgroundColor: "#f9f9f9",
                    boxSizing: "border-box",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
               }}>
                    <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>

                         {/* Personal Details Section */}
                         <div className="form-section" style={cardStyle}>
                              <div className="section-title" style={{ marginBottom: "5px" }}>
                                   Personal Details
                              </div>
                              <div style={grid4Columns}>
                                   <label>
                                        Position Applied
                                        <input
                                             className="form-control input-form"
                                             name="positionApplied"
                                             value={personal.positionApplied}
                                             onChange={handlePersonalChange}
                                             required
                                        />
                                   </label>

                                   <label>
                                        Candidate Name
                                        <input
                                             className="form-control input-form"
                                             name="candidateName"
                                             value={personal.candidateName}
                                             onChange={handlePersonalChange}
                                             required
                                        />
                                   </label>

                                   <label>
                                        Mobile
                                        <input
                                             className="form-control input-form"
                                             name="mobile"
                                             type="text"
                                             value={personal.mobile}
                                             maxLength={10}
                                             required
                                             onChange={(e) => {
                                                  const onlyNums = e.target.value.replace(/\D/g, "");
                                                  handlePersonalChange({
                                                       target: { name: e.target.name, value: onlyNums },
                                                  });
                                             }}
                                        />
                                   </label>

                                   <label>
                                        Email
                                        <input
                                             className="form-control input-form"
                                             name="email"
                                             type="email"
                                             value={personal.email}
                                             onChange={handlePersonalChange}
                                             required
                                        />
                                   </label>

                                   <label>
                                        Educational Qualification
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="qualification"
                                             value={personal.qualification}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Department
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="department"
                                             value={personal.department}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Relevant State Medical Registration
                                        <select
                                             className="form-control input-form"
                                             required
                                             name="registration"
                                             value={personal.registration}
                                             onChange={handlePersonalChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             <option>Yes</option>
                                             <option>No</option>
                                        </select>
                                   </label>

                                   <label>
                                        Paediatric
                                        <select
                                             className="form-control input-form"
                                             required
                                             name="paediatric"
                                             value={personal.paediatric}
                                             onChange={handlePersonalChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             <option>Yes</option>
                                             <option>No</option>
                                        </select>
                                   </label>

                                   <label>
                                        Total Experience (In Yrs.)
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="totalExperience"
                                             value={personal.totalExperience}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        VDC Location (Applied for)
                                        <input
                                             className="form-control input-form"
                                             required
                                             name="vdcLocation"
                                             value={personal.vdcLocation}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Timings From
                                        <input
                                             required
                                             type="time"
                                             className="form-control input-form"
                                             name="timing_from"
                                             value={personal.timing_from}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Timings To
                                        <input
                                             required
                                             type="time"
                                             className="form-control input-form"
                                             name="timing_to"
                                             value={personal.timing_to}
                                             onChange={handlePersonalChange}
                                        />
                                   </label>

                                   <label>
                                        Type of Association
                                        <select
                                             className="form-control input-form"
                                             name="association_type"
                                             value={personal.association_type}
                                             required
                                             onChange={handlePersonalChange}
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">-- Select --</option>
                                             <option value="Full Time">Full Time</option>
                                             <option value="Part Time">Part Time</option>
                                             <option value="Target Model">Target Model</option>
                                             <option value="Base">Base</option>
                                        </select>
                                   </label>



                                   <label>Hr Status
                                        <select
                                             className="form-control input-form"
                                             name="hr_status"
                                             value={personal.hr_status}
                                             onChange={(e) =>
                                                  setPersonal({ ...personal, hr_status: e.target.value })
                                             }
                                             style={{ height: "32px", width: "100%", padding: "0px 18px" }}
                                        >
                                             <option value="">Select Status</option>
                                             <option value="Pending">Pending</option>
                                             <option value="Approved">Approved</option>
                                        </select>
                                   </label>

                              </div>
                         </div>


                    </div>
               </div>



               {/* Form Actions */}
               <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0px" }}>
                    <div className="small-muted">All fields are mandatory.</div>
                    <div className="form-actions">
                         <button
                              type="button"
                              className="btn"
                              style={{ background: "#9CA3AF" }}
                              onClick={handleCancel}
                              disabled={isSubmitting}
                         >
                              Cancel
                         </button>
                         <button
                              type="submit"
                              className="btn btn-warning pull-right"
                              disabled={isSubmitting}
                         >
                              {isSubmitting ? "Updating..." : "Update Assessment"}
                         </button>
                    </div>
               </div>
          </form>
     );
}